/**
 * Sky dome. Analytic gradient + scrolling cloud layers, sharing skyShared.glsl with the
 * ocean so reflections always match the sky overhead.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreateSphereVertexData } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { ShaderStore } from '@babylonjs/core/Engines/shaderStore';
import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Vector2 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';

import skyVert from '../../shaders/sky.vert';
import skyFrag from '../../shaders/sky.frag';

import type { WeatherState } from '../../sim/weather';
import type { Environment } from '../environment';
import { clamp01 } from '../../sim/math';

let registered = false;
function registerShaders(): void {
  if (registered) return;
  ShaderStore.ShadersStore['deepwaterSkyVertexShader'] = skyVert;
  ShaderStore.ShadersStore['deepwaterSkyFragmentShader'] = skyFrag;
  registered = true;
}

export class SkyDome {
  readonly mesh: Mesh;
  private readonly material: ShaderMaterial;

  constructor(scene: Scene, cloudTex: Texture) {
    registerShaders();

    this.mesh = new Mesh('skyDome', scene);
    // Radius is arbitrary — the vertex shader pins the dome to the far plane.
    CreateSphereVertexData({ diameter: 2, segments: 24, sideOrientation: Mesh.BACKSIDE }).applyToMesh(this.mesh);

    this.material = new ShaderMaterial(
      'skyMat',
      scene,
      { vertex: 'deepwaterSky', fragment: 'deepwaterSky' },
      {
        attributes: ['position'],
        uniforms: [
          'viewProjection',
          'uCameraPos',
          'uTime',
          'uWindDir',
          'uWindSpeed',
          'uCloudCover',
          'uStorminess',
          'uRain',
          'uFogAmount',
          'uFogColor',
          'uHorizonHaze',
          'uZenithColor',
          'uHorizonColor',
          'uSunColor',
          'uSunDir',
          'uSunGlowPower',
          'uSunGlowIntensity',
          'uHorizonFalloff',
          'uExposure',
          'uMistAir',
          'uMistAirHeight',
          'uMistSea',
          'uMistHeight',
          'uCameraHeight',
          'uSkyMistStrength',
          'uCloudDither',
        ],
        samplers: ['uCloudTex'],
      },
    );
    this.material.setTexture('uCloudTex', cloudTex);
    // The texture creator knows its own quantum — half-float fields need a fraction of
    // the dither an 8-bit fallback does. The default matches the coarse case so a
    // texture without metadata can never band.
    const meta = cloudTex.metadata as { cloudDither?: number } | null;
    this.material.setFloat('uCloudDither', meta?.cloudDither ?? 0.0085);
    this.material.backFaceCulling = false;
    // Never write depth: everything in the world is in front of the sky.
    this.material.disableDepthWrite = true;

    this.mesh.material = this.material;
    this.mesh.infiniteDistance = true;
    this.mesh.isPickable = false;
    this.mesh.alwaysSelectAsActiveMesh = true;
    // Rendering group -1 draws the sky before the ocean.
    this.mesh.renderingGroupId = 0;
    this.mesh.applyFog = false;
  }

  setEnvironment(env: Environment, weather: WeatherState): void {
    const m = this.material;
    m.setColor3('uZenithColor', env.zenithColor);
    m.setColor3('uHorizonColor', env.horizonColor);
    m.setColor3('uSunColor', env.sunColor);
    m.setVector3('uSunDir', env.sunDir);
    m.setFloat('uSunGlowPower', env.sunGlowPower);
    m.setFloat('uSunGlowIntensity', env.sunGlowIntensity);
    m.setFloat('uHorizonFalloff', env.horizonFalloff);
    m.setColor3('uFogColor', env.fogColor);
    m.setFloat('uHorizonHaze', env.horizonHaze);
    m.setFloat('uCloudCover', weather.cloudCover);
    m.setFloat('uStorminess', clamp01((weather.seaState - 4) / 3));
    m.setFloat('uRain', weather.rain);
    m.setFloat('uFogAmount', weather.fogDensity);
    m.setVector2('uWindDir', new Vector2(Math.sin(weather.windDir), Math.cos(weather.windDir)));
    m.setFloat('uWindSpeed', weather.windSpeed);
    m.setFloat('uExposure', env.exposure);
    m.setFloat('uMistAir', env.mistAirDensity);
    m.setFloat('uMistAirHeight', env.mistAirHeightScale);
    m.setFloat('uMistSea', env.mistSeaDensity);
    m.setFloat('uMistHeight', env.mistHeightScale);
    m.setFloat('uSkyMistStrength', env.skyMistStrength);
  }

  setFrame(time: number, cameraPos: Vector3): void {
    this.material.setFloat('uTime', time);
    this.material.setVector3('uCameraPos', cameraPos);
    this.material.setFloat('uCameraHeight', cameraPos.y);
  }

  dispose(): void {
    this.material.dispose();
    this.mesh.dispose();
  }
}
